import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  canAccessPath,
  getUserRoutesFromStorage,
  normalizePortalPath,
} from './routePrivilegeMap';

interface PrivateRouteProps {
  children: React.ReactElement;
  requireAdmin?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requireAdmin }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');

  let isAdmin = false;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      isAdmin = payload?.isadmin?.toString() === 'true';
    } catch (err) {
      console.error('Invalid token or decoding error', err);
    }
  }

  if (!token) return <Navigate to="/login" />;
  if (requireAdmin && !isAdmin) return <Navigate to="/forbidden" />;

  const currentPath = location.pathname;
  const userRoutes = getUserRoutesFromStorage();

  // Enforce route privileges for portal (and /chat) paths
  if (currentPath.startsWith('/portal') || currentPath.startsWith('/chat')) {
    if (!canAccessPath(currentPath, userRoutes)) {
      const fallback = getAllowedPortalPath(userRoutes);
      if (fallback !== currentPath) {
        return <Navigate to={fallback} replace />;
      }
    }
  }

  return children;
};

function getAllowedPortalPath(routes: ReturnType<typeof getUserRoutesFromStorage>): string {
  try {
    const flatten = (items: typeof routes): string[] =>
      items.flatMap((r) =>
        r.hasAccess
          ? [r.path ?? '', ...(Array.isArray(r.children) ? flatten(r.children) : [])]
          : []
      );

    const paths = flatten(routes)
      .filter((p) => !!p)
      .map((p) => normalizePortalPath(p));

    return (
      paths.find(
        (p) =>
          p !== '/portal/home' &&
          p !== '/chat' &&
          canAccessPath(p, routes)
      ) || '/portal/my-account'
    );
  } catch {
    return '/portal/my-account';
  }
}

export default PrivateRoute;
